/**
 * Universal Semantic ID Contract for DynamicTable automation.
 *
 * Every automation-facing `id` attribute rendered by table-builder MUST
 * come from this file. Hardcoding ID strings in renderers is forbidden.
 *
 * Naming rules:
 *   - camelCase for static IDs:  tableToolbar, tableSearch, selectAll
 *   - dot notation for parameterized IDs:  cell.0.name, row.0.actions, bulkAction.EXPORT
 *   - Legacy exceptions with hyphens:  tableToolbar-filterButton, tableToolbar-resetFilter
 *   - NO version prefix (dt-v2-, v1-, etc.)
 *
 * If you change any value here, the snapshot test in
 * __tests__/semantic-contract.test.ts will fail. Coordinate with the
 * automation team before merging.
 */

export const TABLE_IDS = Object.freeze({
  // -- Structure --
  container: 'dynamicTableContainer',
  table: 'dynamicTable',
  toolbar: 'tableToolbar',

  // -- Search & Filter --
  search: 'tableSearch',
  filterButton: 'tableToolbar-filterButton',
  filterTrigger: 'filterTrigger',
  filterReset: 'tableToolbar-resetFilter',
  filterSelect: (name: string) => `filterSelect.${name}`,
  filterDate: (name: string) => `filterDate.${name}`,
  filterInput: (name: string) => `filterInput.${name}`,

  // -- Cells & Rows --
  cell: (rowIndex: number, field: string) => `cell.${rowIndex}.${field}`,
  row: (rowIndex: number) => `row.${rowIndex}`,
  rowActions: (rowIndex: number) => `row.${rowIndex}.actions`,
  rowCheckbox: (rowIndex: number) => `row.${rowIndex}.select`,
  rowSave: (rowKey: string) => `row.${rowKey}.save`,
  rowCancel: (rowKey: string) => `row.${rowKey}.cancel`,
  expandRow: (rowIndex: number) => `expandRowBtn.${rowIndex}`,

  // -- Sub-rows --
  subRow: (rowIndex: number, subIndex: number) => `subrow.${rowIndex}.${subIndex}`,
  subRowActions: (parentKey: string, subIndex: number) => `subrow.${parentKey}.${subIndex}.actions`,
  subRowCell: (rowIndex: number, subIndex: number, field: string) =>
    `cell.${rowIndex}.${subIndex}.${field}`,

  // -- Group rows --
  groupRow: (groupKey: string) => `group.${groupKey}`,
  expandGroup: (groupKey: string) => `expandGroupBtn.${groupKey}`,
  groupCell: (groupKey: string, field: string) => `group.${groupKey}.${field}`,

  // -- Headers --
  headerCell: (field: string) => `header.${field}`,
  headerCheckbox: 'selectAll',
  selectAll: 'selectAll',

  // -- Actions (row menu items) --
  actionItem: (actionId: string, rowIndex: number) => `action.${actionId}.${rowIndex}`,

  // -- Toolbar actions --
  toolbarPrimary: 'toolbarPrimary',
  toolbarSecondary: (id: string) => `toolbarSecondary.${id}`,

  // -- Bulk toolbar --
  bulkAction: (id: string) => `bulkAction.${id}`,
  bulkSave: 'bulkSave',
  bulkCancel: 'bulkCancel',
  columnPicker: 'columnPicker',
  selectionCount: 'selectionCount',
  selectAllToggle: 'selectAllToggle',
  toggleAllGroups: 'toggleAllGroups',

  // -- Pagination --
  pagination: 'pagination',
  loadMoreButton: 'loadMoreBtn',

  // -- Inline editors --
  editorText: (field: string) => `editor.text.${field}`,
  editorNumber: (field: string) => `editor.number.${field}`,
  editorDate: (field: string) => `editor.date.${field}`,
  editorSelect: (field: string) => `editor.select.${field}`,

  // -- Inline edit (V1 compat — automation depends on these) --
  saveOnEditBtn: 'saveOnEditBtn',
  cancelOnEditBtn: 'cancelOnEditBtn',

  // -- Modals --
  modalConfirm: 'modalConfirm',
  modalCancel: 'modalCancel',

  // -- Empty state --
  emptyState: 'emptyState',
});
